export const DEFAULT_REACTION_OPTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'] as const;

export type ReactionOption = (typeof DEFAULT_REACTION_OPTIONS)[number];

export const REACTION_OPTION_LABELS: Record<ReactionOption, string> = {
  '👍': 'Like',
  '❤️': 'Love',
  '😂': 'Laugh',
  '😮': 'Surprised',
  '😢': 'Sad',
  '🙏': 'Thanks',
};

export type ReactionSummary = {
  count: number;
  mine: boolean;
};

export type ReactionSummaryMap = Record<string, ReactionSummary>;

type ScopedReactionRecord<TScopeKey extends string> = {
  emoji: string | null;
  user_id: string | null;
} & Record<TScopeKey, string | null>;

type FeedLikeRecord = {
  post_id: string | null;
  user_id: string | null;
};

export const groupScopedReactions = <TScopeKey extends string>(
  reactions: Array<ScopedReactionRecord<TScopeKey>>,
  scopeKey: TScopeKey,
  scopeId: string,
  viewerId?: string | null,
): ReactionSummaryMap => {
  const grouped: ReactionSummaryMap = {};

  for (const reaction of reactions) {
    if (!reaction.emoji || reaction[scopeKey] !== scopeId) continue;

    if (!grouped[reaction.emoji]) {
      grouped[reaction.emoji] = { count: 0, mine: false };
    }

    grouped[reaction.emoji].count += 1;

    if (reaction.user_id && viewerId && reaction.user_id === viewerId) {
      grouped[reaction.emoji].mine = true;
    }
  }

  return grouped;
};

export const groupFeedPostReactions = (
  postId: string,
  likes: FeedLikeRecord[],
  reactions: Array<ScopedReactionRecord<'post_id'>>,
  viewerId?: string | null,
): ReactionSummaryMap => {
  const grouped: ReactionSummaryMap = {};
  const likeCount = likes.filter((like) => like.post_id === postId).length;
  const likedByViewer = likes.some((like) => like.post_id === postId && like.user_id === viewerId);

  if (likeCount > 0) {
    grouped['❤️'] = {
      count: likeCount,
      mine: likedByViewer,
    };
  }

  const otherReactions = groupScopedReactions(reactions, 'post_id', postId, viewerId);

  for (const [emoji, value] of Object.entries(otherReactions)) {
    grouped[emoji] = value;
  }

  return grouped;
};

export const orderReactionEntries = (summary: ReactionSummaryMap): Array<[string, ReactionSummary]> => {
  const entries = Object.entries(summary);
  const preferredOrder = new Map(DEFAULT_REACTION_OPTIONS.map((emoji, index) => [emoji, index]));

  return entries.sort(([leftEmoji], [rightEmoji]) => {
    const leftIndex = preferredOrder.get(leftEmoji as ReactionOption);
    const rightIndex = preferredOrder.get(rightEmoji as ReactionOption);

    if (leftIndex === undefined && rightIndex === undefined) {
      return leftEmoji.localeCompare(rightEmoji);
    }

    if (leftIndex === undefined) return 1;
    if (rightIndex === undefined) return -1;
    return leftIndex - rightIndex;
  });
};

export const buildReactionMenuLabel = (emoji: string) => {
  const namedEmoji = REACTION_OPTION_LABELS[emoji as ReactionOption];
  return namedEmoji ? `${emoji} ${namedEmoji}` : emoji;
};
